import {
  Box,
  Heading,
  Select,
  Stack,
  Input,
  Button,
  Text,
  useToast,
  RadioGroup,
  Radio,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { apiFetch } from "./api";   // <-- import apiFetch

const API_BASE = "https://n441mm4tfl.execute-api.eu-west-2.amazonaws.com/prod";

export default function AttachPlanPage() {
  const { keyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const keyName = (location.state as { keyName?: string })?.keyName || "";

  const toast = useToast();

  const [usagePlans, setUsagePlans] = useState<any[]>([]);
  const [apis, setApis] = useState<any[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedApiId, setSelectedApiId] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [newPlanName, setNewPlanName] = useState("");
  const [newPlanDesc, setNewPlanDesc] = useState("");
  const [newPlanRateLimit, setNewPlanRateLimit] = useState("");
  const [newPlanBurstLimit, setNewPlanBurstLimit] = useState("");
  const [newPlanQuotaLimit, setNewPlanQuotaLimit] = useState("");
  const [newPlanQuotaPeriod, setNewPlanQuotaPeriod] = useState("");
  const [mode, setMode] = useState<"existing" | "new">("existing");

  const [loading, setLoading] = useState(true);
  const [creatingPlan, setCreatingPlan] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [addingApiStage, setAddingApiStage] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const planRes = await apiFetch(`${API_BASE}/listusageplans`);
        const plans = await planRes.json();
        setUsagePlans(plans);

        const apiRes = await apiFetch(`${API_BASE}/listapisandstages`);
        const apiData = await apiRes.json();

        // Group by restApiId
        const grouped: Record<string, any> = {};
        apiData.forEach((item: any) => {
          const apiId = item.restApiId;
          if (!grouped[apiId]) {
            grouped[apiId] = { id: apiId, name: item.path, stages: [] };
          }
          if (item.stage && !grouped[apiId].stages.includes(item.stage)) {
            grouped[apiId].stages.push(item.stage);
          }
        });
        setApis(Object.values(grouped));
      } catch (err) {
        toast({ title: "Failed to load data", status: "error" });
      }
      setLoading(false);
    }
    load();
  }, [toast]);

  const handleCreatePlan = async () => {
    setCreatingPlan(true);
    try {
      const payload: any = { name: newPlanName, description: newPlanDesc };
      if (newPlanRateLimit && newPlanBurstLimit) {
        payload.throttle = {
          rateLimit: Number(newPlanRateLimit),
          burstLimit: Number(newPlanBurstLimit),
        };
      }
      if (newPlanQuotaLimit && newPlanQuotaPeriod) {
        payload.quota = {
          limit: Number(newPlanQuotaLimit),
          period: newPlanQuotaPeriod,
        };
      }
      const res = await apiFetch(`${API_BASE}/createusageplan`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      const plan = await res.json();
      setUsagePlans([...usagePlans, plan]);
      setSelectedPlanId(plan.id);
      toast({ title: "Usage Plan Created", status: "success" });
    } catch {
      toast({ title: "Failed to create plan", status: "error" });
    }
    setCreatingPlan(false);
  };

  const handleAttach = async () => {
    if (!keyId || !selectedPlanId) return;
    setAttaching(true);
    try {
      const res = await apiFetch(`${API_BASE}/attachapikeytoplan`, {
        method: "POST",
        body: JSON.stringify({ apiKeyId: keyId, usagePlanId: selectedPlanId }),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Attached successfully", status: "success" });
      navigate("/usage-plans");
    } catch {
      toast({ title: "Failed to attach", status: "error" });
    }
    setAttaching(false);
  };

  const handleAddApiStage = async () => {
    if (!selectedPlanId || !selectedApiId || !selectedStage) return;
    setAddingApiStage(true);
    try {
      const res = await apiFetch(`${API_BASE}/addapistage`, {
        method: "POST",
        body: JSON.stringify({
          usagePlanId: selectedPlanId,
          apiId: selectedApiId,
          stage: selectedStage,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed");

      if (data.message === "API+Stage already exists") {
        toast({ title: "API+Stage already attached to plan", status: "info" });
      } else {
        toast({ title: "API+Stage added", status: "success" });
      }
    } catch {
      toast({ title: "Failed to add API+Stage", status: "error" });
    }
    setAddingApiStage(false);
  };

  if (loading) return <Text>Loading...</Text>;

  return (
    <Box px={10} py={10}>
      <Heading mb={6}>
        Attach Usage Plan{" "}
        {keyName && <Text as="span" color="yellow.400">for "{keyName}"</Text>}
      </Heading>

      {/* Mode toggle */}
      <RadioGroup
        value={mode}
        onChange={(val) => setMode(val as "existing" | "new")}
        mb={4}
      >
        <Stack direction="row" spacing={6}>
          <Radio value="existing">Use Existing Plan</Radio>
          <Radio value="new">Create New Plan</Radio>
        </Stack>
      </RadioGroup>

      {mode === "existing" && (
        <Box mb={4}>
          <Select
            placeholder="Select existing usage plan"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            mb={3}
          >
            {usagePlans.map((plan, idx) => (
              <option key={`${plan.id}-${idx}`} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>

          {selectedPlanId && (() => {
            const plan = usagePlans.find((p) => p.id === selectedPlanId);
            if (!plan) return null;
            return (
              <Box p={3} bg="yellow.100" borderRadius="md" color="black">
                <Text fontWeight="bold">{plan.name}</Text>
                {plan.description && (
                  <Text fontSize="sm">Desc: {plan.description}</Text>
                )}
                {plan.quota && (
                  <Text fontSize="sm">
                    Quota: {plan.quota.limit} per {plan.quota.period}
                  </Text>
                )}
                {plan.throttle && (
                  <Text fontSize="sm">
                    Throttle: {plan.throttle.rateLimit} req/s, Burst{" "}
                    {plan.throttle.burstLimit}
                  </Text>
                )}
              </Box>
            );
          })()}
        </Box>
      )}

      {mode === "new" && (
        <Stack spacing={2} mb={4}>
          <Input
            placeholder="Plan Name"
            value={newPlanName}
            onChange={(e) => setNewPlanName(e.target.value)}
          />
          <Input
            placeholder="Description (optional)"
            value={newPlanDesc}
            onChange={(e) => setNewPlanDesc(e.target.value)}
          />
          <Input
            placeholder="Rate Limit (req/s)"
            type="number"
            value={newPlanRateLimit}
            onChange={(e) => setNewPlanRateLimit(e.target.value)}
          />
          <Input
            placeholder="Burst Limit"
            type="number"
            value={newPlanBurstLimit}
            onChange={(e) => setNewPlanBurstLimit(e.target.value)}
          />
          <Input
            placeholder="Quota Limit"
            type="number"
            value={newPlanQuotaLimit}
            onChange={(e) => setNewPlanQuotaLimit(e.target.value)}
          />
          <Select
            placeholder="Quota Period"
            value={newPlanQuotaPeriod}
            onChange={(e) => setNewPlanQuotaPeriod(e.target.value)}
          >
            <option value="DAY">Day</option>
            <option value="WEEK">Week</option>
            <option value="MONTH">Month</option>
          </Select>
          <Button
            size="sm"
            onClick={handleCreatePlan}
            isLoading={creatingPlan}
            colorScheme="yellow"
            disabled={!newPlanName}
          >
            Create Usage Plan
          </Button>
        </Stack>
      )}

      {/* Attach/Cancel Buttons */}
      <Box mb={6}>
        <Button
          colorScheme="yellow"
          onClick={handleAttach}
          isLoading={attaching}
          disabled={!selectedPlanId}
          mr={3}
        >
          Attach to Plan
        </Button>
        <Button onClick={() => navigate("/usage-plans")}>Cancel</Button>

        <Text fontSize="sm" mt={2} color="red.400">
          You need to associate an API + Stage to the usage plan for it to apply to the selected API key.
        </Text>
      </Box>

      <Text fontWeight={600} mt={4} mb={1}>
        Associate an API + Stage:
      </Text>
      <Stack direction="row" spacing={2} mb={4}>
        <Select
          placeholder="API"
          value={selectedApiId}
          onChange={(e) => {
            setSelectedApiId(e.target.value);
            setSelectedStage("");
          }}
          maxW={160}
        >
          {apis.map((api, idx) => (
            <option key={`${api.id}-${idx}`} value={api.id}>
              {api.name}
            </option>
          ))}
        </Select>
        <Select
          placeholder="Stage"
          value={selectedStage}
          onChange={(e) => setSelectedStage(e.target.value)}
          maxW={160}
          isDisabled={!selectedApiId}
        >
          {apis
            .filter((a) => a.id === selectedApiId)
            .flatMap((api) =>
              api.stages.map((s: string, idx: number) => (
                <option key={`${api.id}-${s}-${idx}`} value={s}>
                  {s}
                </option>
              ))
            )}
        </Select>
        <Button
          size="sm"
          colorScheme="yellow"
          isLoading={addingApiStage}
          disabled={!selectedPlanId || !selectedApiId || !selectedStage}
          onClick={handleAddApiStage}
        >
          Add
        </Button>
      </Stack>
    </Box>
  );
}
